import { createRandomId } from "RandomId";
import { isCanvaShortLinkUrl, normalizeCanvaManualUrl } from "./canvaUtils";
import { isGoogleSlidesPresentationUrl } from "./googleSlidesLinks";
import { validatePowerpointEmbedInput } from "./powerpointEmbedUtils";

export const AGENDA_RESOURCE_TYPES = {
  LINK: "link",
  YOUTUBE: "youtube",
  FILE: "file",
  AUDIO: "audio",
  IMAGE: "image",
  NOTE: "note",
  CONTENT_BOX: "contentBox",
};

/** Matches Quick Launch link open targets (`app` | `newTab`). */
export const AGENDA_RESOURCE_OPEN_TARGETS = {
  APP: "app",
  NEW_TAB: "newTab",
};

export const AGENDA_GUIDED_LINK_RESOURCE_KINDS = {
  LINK: "link",
  GOOGLE_SLIDES: "googleSlides",
  CANVA: "canva",
  POWERPOINT: "powerPoint",
};

export const AGENDA_IMAGE_RESOURCE_HEADING_MAX_LENGTH = 100;

/** Length cap only. Do not trim — live inputs need trailing spaces while typing. */
export const clampAgendaImageResourceHeading = (value = "") => (
  String(value || "").slice(0, AGENDA_IMAGE_RESOURCE_HEADING_MAX_LENGTH)
);
export const AGENDA_NOTE_RESOURCE_TEXT_MAX_LENGTH = 5000;
export const AGENDA_NOTE_RESOURCE_FONT_SIZE_MIN = 10;
export const AGENDA_NOTE_RESOURCE_FONT_SIZE_MAX = 80;
export const AGENDA_NOTE_RESOURCE_DEFAULTS = {
  title: "Note",
  text: "",
  fontSize: 24,
  fontColor: "#111827",
  backgroundColor: "#fff7cc",
};

/** Persist / display. Trim only after the teacher is done typing. */
export const normalizeAgendaImageResourceHeading = (value = "") => (
  clampAgendaImageResourceHeading(value).trim()
);

export const normalizeAgendaNoteResourceTitle = () => AGENDA_NOTE_RESOURCE_DEFAULTS.title;

export const normalizeAgendaNoteResourceText = (value = "") => (
  String(value || "").slice(0, AGENDA_NOTE_RESOURCE_TEXT_MAX_LENGTH)
);

export const normalizeAgendaNoteResourceFontSize = (value) => {
  const fontSize = Number(value);
  if (!Number.isFinite(fontSize)) return AGENDA_NOTE_RESOURCE_DEFAULTS.fontSize;
  return Math.max(
    AGENDA_NOTE_RESOURCE_FONT_SIZE_MIN,
    Math.min(AGENDA_NOTE_RESOURCE_FONT_SIZE_MAX, Math.round(fontSize))
  );
};

export const normalizeAgendaNoteResourceColor = (value, fallback) => {
  const color = String(value || "").trim();
  return color || fallback;
};

export const normalizeAgendaResourceOpenTarget = (value) => (
  value === AGENDA_RESOURCE_OPEN_TARGETS.NEW_TAB
    ? AGENDA_RESOURCE_OPEN_TARGETS.NEW_TAB
    : AGENDA_RESOURCE_OPEN_TARGETS.APP
);

/** Content boxes always open in-app; other launchable types can choose app vs new tab. */
export const canAgendaResourceChooseOpenTarget = (resource = {}) => {
  if (!resource || typeof resource !== "object") return false;

  switch (resource.type) {
    case AGENDA_RESOURCE_TYPES.LINK:
    case AGENDA_RESOURCE_TYPES.YOUTUBE:
    case AGENDA_RESOURCE_TYPES.AUDIO:
      return true;
    case AGENDA_RESOURCE_TYPES.FILE:
      return resource.fileType === "pdf";
    case AGENDA_RESOURCE_TYPES.CONTENT_BOX:
    default:
      return false;
  }
};

export const shouldOpenAgendaResourceInNewTab = (resource = {}) => (
  canAgendaResourceChooseOpenTarget(resource)
  && normalizeAgendaResourceOpenTarget(resource.openTarget) === AGENDA_RESOURCE_OPEN_TARGETS.NEW_TAB
);

export const normalizeResourceUrl = (value = "") => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) return "";
  if (/^\/(?!\/)/.test(trimmedValue)) return trimmedValue;
  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
};

export const isInternalResourceUrl = (value = "") => /^\/(?!\/)/.test(String(value || "").trim());

const GUIDED_RESOURCE_IFRAME_SRC_REGEX = /<iframe\b[^>]*\bsrc\s*=\s*["']([^"']+)["']/i;

const getGuidedResourceUrlCandidate = (value = "") => {
  const text = String(value || "").trim();
  const iframeSrc = text.match(GUIDED_RESOURCE_IFRAME_SRC_REGEX)?.[1]?.replace(/&amp;/gi, "&").trim();
  return iframeSrc || text;
};

export const isValidResourceUrl = (value = "") => {
  try {
    const normalizedUrl = normalizeResourceUrl(value);
    if (!normalizedUrl) return false;
    if (isInternalResourceUrl(normalizedUrl)) return true;
    new URL(normalizedUrl);
    return true;
  } catch (_) {
    return false;
  }
};

export const isCanvaResourceUrl = (value = "") => {
  return Boolean(normalizeCanvaManualUrl(value) || isCanvaShortLinkUrl(value));
};

export const isPowerPointResourceUrl = (value = "") => {
  return validatePowerpointEmbedInput(getGuidedResourceUrlCandidate(value)).valid;
};

export const normalizeGuidedAgendaLinkResourceKind = (value) => (
  Object.values(AGENDA_GUIDED_LINK_RESOURCE_KINDS).includes(value)
    ? value
    : AGENDA_GUIDED_LINK_RESOURCE_KINDS.LINK
);

export const getGuidedAgendaLinkResourceConfig = (kind = AGENDA_GUIDED_LINK_RESOURCE_KINDS.LINK) => {
  switch (normalizeGuidedAgendaLinkResourceKind(kind)) {
    case AGENDA_GUIDED_LINK_RESOURCE_KINDS.GOOGLE_SLIDES:
      return {
        kind: AGENDA_GUIDED_LINK_RESOURCE_KINDS.GOOGLE_SLIDES,
        label: "Google Slides",
        defaultTitle: "Google Slides",
        iconClass: "fa fa-google",
        urlLabel: "Google Slides URL",
        placeholder: "https://docs.google.com/presentation/d/...",
        invalidMessage: "Enter a valid Google Slides presentation URL.",
      };
    case AGENDA_GUIDED_LINK_RESOURCE_KINDS.CANVA:
      return {
        kind: AGENDA_GUIDED_LINK_RESOURCE_KINDS.CANVA,
        label: "Canva",
        defaultTitle: "Canva",
        iconClass: "canva-brand-icon",
        urlLabel: "Canva URL or embed code",
        placeholder: "https://www.canva.com/design/...",
        invalidMessage: "Enter a valid Canva URL or iframe embed code.",
      };
    case AGENDA_GUIDED_LINK_RESOURCE_KINDS.POWERPOINT:
      return {
        kind: AGENDA_GUIDED_LINK_RESOURCE_KINDS.POWERPOINT,
        label: "PowerPoint",
        defaultTitle: "PowerPoint",
        iconClass: "fa fa-file-powerpoint-o",
        urlLabel: "PowerPoint embed URL",
        placeholder: "https://view.officeapps.live.com/op/embed.aspx?src=...",
        invalidMessage: "Enter a valid Microsoft/OneDrive/SharePoint PowerPoint embed URL.",
      };
    case AGENDA_GUIDED_LINK_RESOURCE_KINDS.LINK:
    default:
      return {
        kind: AGENDA_GUIDED_LINK_RESOURCE_KINDS.LINK,
        label: "Link",
        defaultTitle: "Resource Link",
        iconClass: "fa fa-link",
        urlLabel: "Link URL",
        placeholder: "https://example.com",
        invalidMessage: "Enter a valid URL.",
      };
  }
};

export const getGuidedAgendaLinkResourceKindForUrl = (value = "") => {
  if (isGoogleSlidesPresentationUrl(value)) {
    return AGENDA_GUIDED_LINK_RESOURCE_KINDS.GOOGLE_SLIDES;
  }
  if (isCanvaResourceUrl(value)) {
    return AGENDA_GUIDED_LINK_RESOURCE_KINDS.CANVA;
  }
  if (isPowerPointResourceUrl(value)) {
    return AGENDA_GUIDED_LINK_RESOURCE_KINDS.POWERPOINT;
  }
  return AGENDA_GUIDED_LINK_RESOURCE_KINDS.LINK;
};

export const isValidGuidedAgendaLinkResourceUrl = (kind, value = "") => {
  const normalizedKind = normalizeGuidedAgendaLinkResourceKind(kind);
  if (normalizedKind === AGENDA_GUIDED_LINK_RESOURCE_KINDS.GOOGLE_SLIDES) {
    return isGoogleSlidesPresentationUrl(value);
  }
  if (normalizedKind === AGENDA_GUIDED_LINK_RESOURCE_KINDS.CANVA) {
    return isCanvaResourceUrl(value);
  }
  if (normalizedKind === AGENDA_GUIDED_LINK_RESOURCE_KINDS.POWERPOINT) {
    return isPowerPointResourceUrl(value);
  }
  return isValidResourceUrl(value);
};

export const isYouTubeVideoHost = (hostname = "") => {
  const host = String(hostname || "").replace(/^www\./i, "").toLowerCase();
  return (
    host === "youtu.be"
    || host === "youtube.com"
    || host.endsWith(".youtube.com")
    || host === "youtube-nocookie.com"
    || host.endsWith(".youtube-nocookie.com")
  );
};

export const getYouTubeVideoId = (value = "") => {
  try {
    const parsedUrl = new URL(normalizeResourceUrl(value));
    const hostname = parsedUrl.hostname.replace(/^www\./i, "").toLowerCase();

    if (hostname === "youtu.be") {
      return parsedUrl.pathname.split("/").filter(Boolean)[0] || "";
    }

    if (!isYouTubeVideoHost(hostname)) {
      return "";
    }

    if (parsedUrl.pathname === "/watch") {
      return parsedUrl.searchParams.get("v") || "";
    }

    const [section, videoId] = parsedUrl.pathname.split("/").filter(Boolean);
    if (["embed", "shorts", "live"].includes(section)) {
      return videoId || "";
    }
  } catch (_) {
    return "";
  }

  return "";
};

export const isValidYouTubeResourceUrl = (value = "") => Boolean(getYouTubeVideoId(value));

export const getYouTubeThumbnailUrl = (value = "") => {
  const videoId = getYouTubeVideoId(value);
  return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : "";
};

export const getAgendaResourceUrlFilename = (url = "") => {
  try {
    const parsedUrl = new URL(url);
    const name = parsedUrl.pathname.split("/").filter(Boolean).pop();
    if (!name) return "";
    return decodeURIComponent(name).split("/").filter(Boolean).pop() || "";
  } catch (_) {
    return "";
  }
};

export const getAgendaResourcePreviewUrl = (value = "") => {
  const rawValue = String(value || "").trim();
  if (!rawValue) return "";
  if (isInternalResourceUrl(rawValue)) return rawValue;

  try {
    const parsedUrl = new URL(normalizeResourceUrl(rawValue));
    const hostname = parsedUrl.hostname.replace(/^www\./i, "");
    const pathname = parsedUrl.pathname === "/" ? "" : parsedUrl.pathname;
    return `${hostname}${pathname}`;
  } catch (_) {
    return rawValue;
  }
};

export const getYouTubeEmbedUrl = (value = "", options = {}) => {
  const videoId = getYouTubeVideoId(value);
  if (!videoId) return "";

  const autoplay = Boolean(options?.autoplay);

  try {
    const parsedUrl = new URL(normalizeResourceUrl(value));
    const startTime = parsedUrl.searchParams.get("start") || parsedUrl.searchParams.get("t");
    const searchParams = new URLSearchParams({
      rel: "0",
      modestbranding: "1",
      playsinline: "1",
      ...(autoplay ? { autoplay: "1" } : {}),
    });

    if (startTime) {
      searchParams.set("start", startTime);
    }

    return `https://www.youtube.com/embed/${videoId}?${searchParams.toString()}`;
  } catch (_) {
    const fallbackParams = autoplay ? "?autoplay=1" : "";
    return `https://www.youtube.com/embed/${videoId}${fallbackParams}`;
  }
};

export const getResourceTypeLabel = (resource = {}) => {
  switch (resource.type) {
    case AGENDA_RESOURCE_TYPES.AUDIO:
      return "Audio";
    case AGENDA_RESOURCE_TYPES.IMAGE:
      return "Image";
    case AGENDA_RESOURCE_TYPES.NOTE:
      return "Note";
    case AGENDA_RESOURCE_TYPES.CONTENT_BOX:
      return "Box";
    case AGENDA_RESOURCE_TYPES.FILE:
      return resource.fileType === "pdf" ? "PDF" : "File";
    case AGENDA_RESOURCE_TYPES.YOUTUBE:
      return "YouTube";
    case AGENDA_RESOURCE_TYPES.LINK:
      return getGuidedAgendaLinkResourceConfig(
        getGuidedAgendaLinkResourceKindForUrl(resource.url)
      ).label;
    default:
      return "Link";
  }
};

export const getResourceIconClass = (resource = {}) => {
  switch (resource.type) {
    case AGENDA_RESOURCE_TYPES.AUDIO:
      return "fa fa-volume-up";
    case AGENDA_RESOURCE_TYPES.IMAGE:
      return "fa fa-picture-o";
    case AGENDA_RESOURCE_TYPES.NOTE:
      return "fa fa-sticky-note";
    case AGENDA_RESOURCE_TYPES.CONTENT_BOX:
      return "fa fa-th-large";
    case AGENDA_RESOURCE_TYPES.FILE:
      return resource.fileType === "pdf" ? "fa fa-file-pdf-o" : "fa fa-file-o";
    case AGENDA_RESOURCE_TYPES.YOUTUBE:
      return "fa fa-youtube-play";
    case AGENDA_RESOURCE_TYPES.LINK:
      return getGuidedAgendaLinkResourceConfig(
        getGuidedAgendaLinkResourceKindForUrl(resource.url)
      ).iconClass;
    default:
      return "fa fa-link";
  }
};

const getUrlFilename = getAgendaResourceUrlFilename;

export const canAgendaResourceAutoDeploy = (resource = {}) => {
  if (!resource || typeof resource !== "object") return false;

  switch (resource.type) {
    case AGENDA_RESOURCE_TYPES.LINK:
    case AGENDA_RESOURCE_TYPES.YOUTUBE:
    case AGENDA_RESOURCE_TYPES.AUDIO:
    case AGENDA_RESOURCE_TYPES.IMAGE:
    case AGENDA_RESOURCE_TYPES.NOTE:
    case AGENDA_RESOURCE_TYPES.CONTENT_BOX:
      return true;
    case AGENDA_RESOURCE_TYPES.FILE:
      return resource.fileType === "pdf";
    default:
      return false;
  }
};

const pickAgendaResourceAutomationFlags = (resource = {}) => ({
  ...(resource.autoDeployOnActivate ? { autoDeployOnActivate: true } : {}),
  ...(resource.autoCloseOnDeactivate ? { autoCloseOnDeactivate: true } : {}),
});

export const normalizeAgendaResource = (resource = {}) => {
  const type = Object.values(AGENDA_RESOURCE_TYPES).includes(resource.type)
    ? resource.type
    : AGENDA_RESOURCE_TYPES.LINK;
  const id = String(resource.id || createRandomId());
  const rawUrl = String(resource.url || resource.path || resource.src || "").trim();
  const shouldNormalizeUrl = [AGENDA_RESOURCE_TYPES.LINK, AGENDA_RESOURCE_TYPES.YOUTUBE].includes(type);
  const url = shouldNormalizeUrl ? normalizeResourceUrl(rawUrl) : rawUrl;
  const detectedGuidedLinkKind = type === AGENDA_RESOURCE_TYPES.LINK
    ? getGuidedAgendaLinkResourceKindForUrl(url)
    : AGENDA_GUIDED_LINK_RESOURCE_KINDS.LINK;
  const detectedLinkTitle = detectedGuidedLinkKind !== AGENDA_GUIDED_LINK_RESOURCE_KINDS.LINK
    ? getGuidedAgendaLinkResourceConfig(detectedGuidedLinkKind).defaultTitle
    : "";
  const fallbackTitle = resource.name
    || resource.label
    || detectedLinkTitle
    || (type === AGENDA_RESOURCE_TYPES.YOUTUBE ? "YouTube Video" : getUrlFilename(url))
    || getResourceTypeLabel({ type, url });
  const automationFlags = pickAgendaResourceAutomationFlags(resource);

  if (type === AGENDA_RESOURCE_TYPES.CONTENT_BOX) {
    const contentBoxId = String(resource.contentBoxId || "").trim();
    if (!contentBoxId) return null;
    return {
      id,
      type,
      title: String(resource.title || resource.label || "Content Box"),
      contentBoxId,
      classId: resource.classId ? String(resource.classId) : "",
      ...automationFlags,
    };
  }

  if (type === AGENDA_RESOURCE_TYPES.NOTE) {
    return {
      id,
      type,
      title: normalizeAgendaNoteResourceTitle(),
      text: normalizeAgendaNoteResourceText(resource.text),
      fontSize: normalizeAgendaNoteResourceFontSize(resource.fontSize),
      fontColor: normalizeAgendaNoteResourceColor(
        resource.fontColor,
        AGENDA_NOTE_RESOURCE_DEFAULTS.fontColor
      ),
      backgroundColor: normalizeAgendaNoteResourceColor(
        resource.backgroundColor,
        AGENDA_NOTE_RESOURCE_DEFAULTS.backgroundColor
      ),
      ...automationFlags,
    };
  }

  if (!url) return null;

  return {
    id,
    type,
    title: String(resource.title || fallbackTitle),
    url,
    ...(type === AGENDA_RESOURCE_TYPES.IMAGE && clampAgendaImageResourceHeading(resource.heading)
      ? { heading: clampAgendaImageResourceHeading(resource.heading) }
      : {}),
    ...(type === AGENDA_RESOURCE_TYPES.FILE ? { fileType: resource.fileType || "file" } : {}),
    ...(canAgendaResourceChooseOpenTarget({ type, fileType: resource.fileType })
      ? { openTarget: normalizeAgendaResourceOpenTarget(resource.openTarget) }
      : {}),
    ...automationFlags,
  };
};

export const getAgendaItemResources = (item = {}) => {
  const agendaItem = item || {};
  const resources = Array.isArray(agendaItem.resources)
    ? agendaItem.resources.map(normalizeAgendaResource).filter(Boolean)
    : [];
  const legacyLink = normalizeResourceUrl(agendaItem.resourceLink || "");

  if (legacyLink && !resources.some((resource) => resource.type === AGENDA_RESOURCE_TYPES.LINK && resource.url === legacyLink)) {
    resources.unshift({
      id: `legacy-link-${legacyLink}`,
      type: AGENDA_RESOURCE_TYPES.LINK,
      title: "Resource Link",
      url: legacyLink,
      isLegacyResourceLink: true,
    });
  }

  return resources;
};

export const createAgendaResource = (resource = {}) => {
  const shouldDefaultAutoDeploy = resource.autoDeployOnActivate === undefined;
  const shouldDefaultAutoClose = resource.autoCloseOnDeactivate === undefined;
  const isNewTabOpenTarget = canAgendaResourceChooseOpenTarget(resource)
    && normalizeAgendaResourceOpenTarget(resource.openTarget) === AGENDA_RESOURCE_OPEN_TARGETS.NEW_TAB;
  const normalized = normalizeAgendaResource({
    ...resource,
    id: resource.id || createRandomId(),
    ...(shouldDefaultAutoDeploy ? { autoDeployOnActivate: true } : {}),
    ...(shouldDefaultAutoClose && !isNewTabOpenTarget ? { autoCloseOnDeactivate: true } : {}),
  });

  if (!normalized) return null;
  if (canAgendaResourceAutoDeploy(normalized)) return normalized;

  const { autoDeployOnActivate, autoCloseOnDeactivate, ...rest } = normalized;
  return rest;
};

const pickRuntimeStepFields = (runtimeItem = {}) => ({
  active: Boolean(runtimeItem.active),
  timeElapsed: Number(runtimeItem.timeElapsed) || 0,
  completed: Boolean(runtimeItem.completed),
});

/**
 * Keep Present runtime progress (active / elapsed / completed) while adopting the
 * latest saved content fields (titles, durations, resources, alerts, etc.).
 * Without this, editor saves can land in Firestore + the preview but never reach
 * the live Present boxData / localStorage restore path.
 */
export const mergeAgendaContentIntoRuntimeState = (content, runtimeBoxData) => {
  if (!content) return runtimeBoxData || null;

  const contentSteps = Array.isArray(content.steps) ? content.steps : [];
  const runtimeStepsById = new Map(
    (runtimeBoxData?.steps || [])
      .filter((step) => step?.stepId)
      .map((step) => [step.stepId, step])
  );

  const mergedSteps = contentSteps.map((contentStep) => {
    const runtimeStep = runtimeStepsById.get(contentStep?.stepId);
    const runtimeSubStepsById = new Map(
      (runtimeStep?.subTaskSteps || [])
        .filter((subStep) => subStep?.stepId)
        .map((subStep) => [subStep.stepId, subStep])
    );

    return {
      ...contentStep,
      ...(runtimeStep ? pickRuntimeStepFields(runtimeStep) : {
        active: false,
        timeElapsed: 0,
        completed: false,
      }),
      subTaskSteps: (contentStep.subTaskSteps || []).map((contentSubStep) => {
        const runtimeSubStep = runtimeSubStepsById.get(contentSubStep?.stepId);
        return {
          ...contentSubStep,
          ...(runtimeSubStep ? pickRuntimeStepFields(runtimeSubStep) : {
            active: false,
            timeElapsed: 0,
            completed: false,
          }),
        };
      }),
    };
  });

  return {
    ...content,
    steps: mergedSteps,
  };
};

export const getActiveAgendaActivationKey = (steps) => {
  if (!Array.isArray(steps)) return null;

  const activeStepIndex = steps.findIndex((step) => step?.active);
  if (activeStepIndex === -1) return null;

  const activeStep = steps[activeStepIndex];
  const activeSubStepIndex = Array.isArray(activeStep?.subTaskSteps)
    ? activeStep.subTaskSteps.findIndex((subTask) => subTask?.active)
    : -1;

  return `${activeStep?.stepId ?? activeStepIndex}:${activeSubStepIndex}`;
};

export const parseActiveAgendaActivationKey = (activationKey) => {
  if (!activationKey || typeof activationKey !== "string") {
    return { stepId: null, subStepIndex: -1 };
  }

  const separatorIndex = activationKey.lastIndexOf(":");
  if (separatorIndex <= 0) {
    return { stepId: activationKey, subStepIndex: -1 };
  }

  const stepId = activationKey.slice(0, separatorIndex);
  const subStepIndex = Number(activationKey.slice(separatorIndex + 1));

  return {
    stepId,
    subStepIndex: Number.isFinite(subStepIndex) ? subStepIndex : -1,
  };
};

export const AGENDA_AUTO_LAUNCH_COUNTDOWN_MAX = 9;

export const getAgendaItemDurationSeconds = (item = {}) => {
  const minutes = Number(item?.minutes);
  const seconds = Number(item?.seconds);
  const safeMinutes = Number.isFinite(minutes) ? minutes : 0;
  const safeSeconds = Number.isFinite(seconds) ? seconds : 0;
  return Math.max(0, safeMinutes * 60 + safeSeconds);
};

export const getActiveAgendaItemRuntimeInfo = (steps = []) => {
  if (!Array.isArray(steps)) return null;

  const activeStepIndex = steps.findIndex((step) => step?.active);
  if (activeStepIndex === -1) return null;

  const activeStep = steps[activeStepIndex];
  const activeSubStepIndex = Array.isArray(activeStep?.subTaskSteps)
    ? activeStep.subTaskSteps.findIndex((subTask) => subTask?.active)
    : -1;
  const activeItem = activeSubStepIndex >= 0
    ? activeStep.subTaskSteps[activeSubStepIndex]
    : activeStep;
  const totalSeconds = getAgendaItemDurationSeconds(activeItem);
  const timeElapsed = Number(activeItem?.timeElapsed) || 0;

  return {
    activeStepIndex,
    activeSubStepIndex,
    totalSeconds,
    secondsRemaining: Math.max(0, Math.ceil(totalSeconds - timeElapsed)),
  };
};

export const getPredictedNextAgendaActivationKey = (steps = []) => {
  const activeInfo = getActiveAgendaItemRuntimeInfo(steps);
  if (!activeInfo) return null;

  const { activeStepIndex, activeSubStepIndex } = activeInfo;
  const activeStep = steps[activeStepIndex];
  const subSteps = Array.isArray(activeStep?.subTaskSteps) ? activeStep.subTaskSteps : [];

  if (activeSubStepIndex >= 0 && activeSubStepIndex + 1 < subSteps.length) {
    return `${activeStep?.stepId ?? activeStepIndex}:${activeSubStepIndex + 1}`;
  }

  const nextStepIndex = activeStepIndex + 1;
  const nextStep = steps[nextStepIndex];
  if (!nextStep) return null;

  const nextSubSteps = Array.isArray(nextStep?.subTaskSteps) ? nextStep.subTaskSteps : [];
  return `${nextStep?.stepId ?? nextStepIndex}:${nextSubSteps.length > 0 ? 0 : -1}`;
};

/**
 * Single-digit stop-sign countdown for upcoming auto-launch.
 * Visible from 9..1 seconds remaining (avoids double-digit "10").
 */
export const getAgendaAutoLaunchCountdownDigit = (secondsRemaining) => {
  const remaining = Math.ceil(Number(secondsRemaining));
  if (!Number.isFinite(remaining)) return null;
  if (remaining < 1 || remaining > AGENDA_AUTO_LAUNCH_COUNTDOWN_MAX) return null;
  return remaining;
};

const getResourcesFromItemByAutomationFlag = (item, flag) =>
  getAgendaItemResources(item).filter(
    (resource) => resource[flag] && canAgendaResourceAutoDeploy(resource)
  );

const getAutoDeployResourcesFromItem = (item) =>
  getResourcesFromItemByAutomationFlag(item, "autoDeployOnActivate");

const getAutoCloseResourcesFromItem = (item) =>
  getResourcesFromItemByAutomationFlag(item, "autoCloseOnDeactivate");

const pushUniqueResources = (target, resources, seenIds) => {
  resources.forEach((resource) => {
    if (seenIds.has(resource.id)) return;
    seenIds.add(resource.id);
    target.push(resource);
  });
};

/**
 * When the active agenda step/substep changes, collect resources marked to
 * auto-deploy. Step-level resources only fire when the step itself changes
 * (not when moving between that step's substeps).
 */
export const collectAutoDeployResourcesForActivationTransition = ({
  steps = [],
  previousKey = null,
  nextKey = null,
} = {}) => {
  if (!nextKey || nextKey === previousKey || !Array.isArray(steps)) {
    return [];
  }

  const previous = parseActiveAgendaActivationKey(previousKey);
  const next = parseActiveAgendaActivationKey(nextKey);
  const nextStepIndex = steps.findIndex(
    (step, index) => String(step?.stepId ?? index) === String(next.stepId)
  );
  if (nextStepIndex < 0) return [];

  const nextStep = steps[nextStepIndex];
  const collected = [];
  const seenIds = new Set();

  const pushResources = (item) => {
    pushUniqueResources(collected, getAutoDeployResourcesFromItem(item), seenIds);
  };

  if (previous.stepId !== next.stepId) {
    pushResources(nextStep);
  }

  if (next.subStepIndex >= 0) {
    const nextSubStep = nextStep?.subTaskSteps?.[next.subStepIndex];
    if (nextSubStep) {
      pushResources(nextSubStep);
    }
  }

  return collected;
};

export const collectAutoCloseResourcesForDeactivationTransition = ({
  steps = [],
  previousKey = null,
  nextKey = null,
} = {}) => {
  if (!previousKey || previousKey === nextKey || !Array.isArray(steps)) {
    return [];
  }

  const previous = parseActiveAgendaActivationKey(previousKey);
  const next = parseActiveAgendaActivationKey(nextKey);
  const previousStepIndex = steps.findIndex(
    (step, index) => String(step?.stepId ?? index) === String(previous.stepId)
  );
  if (previousStepIndex < 0) return [];

  const previousStep = steps[previousStepIndex];
  const collected = [];
  const seenIds = new Set();

  if (previous.subStepIndex >= 0) {
    const previousSubStep = previousStep?.subTaskSteps?.[previous.subStepIndex];
    if (previousSubStep) {
      pushUniqueResources(collected, getAutoCloseResourcesFromItem(previousSubStep), seenIds);
    }
  }

  if (previous.stepId !== next.stepId) {
    pushUniqueResources(collected, getAutoCloseResourcesFromItem(previousStep), seenIds);
  }

  return collected;
};

export const buildAgendaResourceTransitionPlan = ({
  steps = [],
  previousKey = null,
  nextKey = null,
} = {}) => {
  const opens = collectAutoDeployResourcesForActivationTransition({ steps, previousKey, nextKey });
  const closes = collectAutoCloseResourcesForDeactivationTransition({ steps, previousKey, nextKey });

  return {
    opens,
    closes,
    coalescedKeepOpenCandidates: closes.length > 0 && opens.length > 0
      ? [{ closes, opens }]
      : [],
  };
};

export const buildAgendaAutoLaunchCountdownState = ({
  steps = [],
  currentKey = null,
  isRunning = false,
  isPaused = false,
  durationInputMode = "duration",
} = {}) => {
  if (
    !isRunning
    || isPaused
    || durationInputMode === "none"
    || !currentKey
    || !Array.isArray(steps)
  ) {
    return null;
  }

  const activeInfo = getActiveAgendaItemRuntimeInfo(steps);
  const nextKey = getPredictedNextAgendaActivationKey(steps);
  if (!activeInfo || !nextKey) return null;

  const digit = getAgendaAutoLaunchCountdownDigit(activeInfo.secondsRemaining);
  if (digit == null) return null;

  const plan = buildAgendaResourceTransitionPlan({
    steps,
    previousKey: currentKey,
    nextKey,
  });
  if (!plan.opens.length) return null;

  const next = parseActiveAgendaActivationKey(nextKey);
  const nextStepIndex = steps.findIndex(
    (step, index) => String(step?.stepId ?? index) === String(next.stepId)
  );

  return {
    digit,
    nextKey,
    nextStepIndex,
    nextSubStepIndex: next.subStepIndex,
    opens: plan.opens,
    openResourceIds: plan.opens.map((resource) => resource.id).filter(Boolean),
    openTitles: plan.opens.map((resource) => resource.title).filter(Boolean),
    transitionKey: `${currentKey || "none"}>${nextKey || "none"}`,
  };
};
